import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { useDialog } from "app/hook";
import { RootState, AppDispatch } from "app/store";
import { AppPlatform, DialogType, AnalyticsEventType } from "app/types";
import I18n from "common/i18n";
import { UserPageTab, PlanSubTab } from "features/user/page/UserPageTab";
import { createHostedCheckout, useSubscriptionPlans } from "./api";
import { PurchaseAudience, setPurchaseAudience } from "./purchaseContext";
import { PlanCategory, BillingPeriod, useNativePackages } from "./useNativePackages";
import {
  getPurchaseStore,
  recordPurchaseStart,
  recordCheckoutReturn,
  recordPurchaseResult,
} from "./purchaseAnalytics";

const IOS_MIN_BUILD = 113;
const ANDROID_MIN_BUILD = 87;

const FALLBACK_PRICES: Record<
  PlanCategory,
  Record<BillingPeriod, { price: number; priceString: string; currencyCode: string }>
> = {
  standard: {
    monthly: { price: 400, priceString: "¥400", currencyCode: "JPY" },
    annual: { price: 3900, priceString: "¥3,900", currencyCode: "JPY" },
  },
  premium: {
    monthly: { price: 1500, priceString: "¥1,500", currencyCode: "JPY" },
    annual: { price: 14900, priceString: "¥14,900", currencyCode: "JPY" },
  },
};

const showToast = (message: string) => {
  toast(
    <div>
      <p className="text text-textcolor-black">{message}</p>
    </div>,
  );
};

export type UseUpgradePurchaseResult = {
  // source は購入を始めた画面（GA の PURCHASE_START などに付ける）
  purchase: (
    planCategory: PlanCategory,
    billingPeriod: BillingPeriod,
    source: string,
  ) => Promise<void>;
  showVersionWarning: boolean;
  closeVersionWarning: () => void;
  gotoStoreUpdate: () => void;
  getPriceDisplay: (
    planCategory: PlanCategory,
    billingPeriod: BillingPeriod,
  ) => { price: number; priceString: string; currencyCode: string };
  isNativeApp: boolean;
  packagesStatus: ReturnType<typeof useNativePackages>["status"];
};

export const useUpgradePurchase = (options?: {
  successUrl?: string;
  cancelUrl?: string;
  /** 購入を始めた画面。結果のトーストの文言を決める（PurchaseResultHandler が受け取る） */
  audience?: PurchaseAudience;
}): UseUpgradePurchaseResult => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const user = useSelector((state: RootState) => state.user.parent);
  const platform = (window.kidsAppPlatform as AppPlatform) || AppPlatform.Web;
  const isNativeApp = platform === AppPlatform.iOS || platform === AppPlatform.Android;

  const { requestPackages, getPrice, getIdentifier, status: packagesStatus } = useNativePackages();

  const { data: webPlans = [] } = useSubscriptionPlans("web", !isNativeApp);

  const [showVersionWarning, setShowVersionWarning] = useState(false);

  useEffect(() => {
    if (isNativeApp) {
      requestPackages();
    }
  }, [isNativeApp, requestPackages]);

  // bfcache 復元時の LOADING クリア
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !window.ReactNativeWebView) {
        handleDialog({ type: DialogType.LOADING, value: false });
        // Stripe からブラウザの戻るで戻った
        recordCheckoutReturn();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [handleDialog]);

  const getPriceDisplay = useCallback(
    (planCategory: PlanCategory, billingPeriod: BillingPeriod) => {
      if (isNativeApp) {
        return getPrice(planCategory, billingPeriod);
      }
      const targetName = planCategory === "standard" ? "基本プラン" : "特別プラン";
      const found = webPlans.find(
        (p) => p.name.includes(targetName) && p.billing_period === billingPeriod,
      );
      if (found) {
        const intPrice = Math.floor(found.price);
        return {
          price: intPrice,
          priceString: `¥${intPrice.toLocaleString()}`,
          currencyCode: "JPY",
        };
      }
      return FALLBACK_PRICES[planCategory][billingPeriod];
    },
    [isNativeApp, getPrice, webPlans],
  );

  const getProductId = useCallback(
    (planCategory: PlanCategory, billingPeriod: BillingPeriod): string | undefined => {
      if (isNativeApp) {
        return getIdentifier(planCategory, billingPeriod);
      }
      const targetName = planCategory === "standard" ? "基本プラン" : "特別プラン";
      const found = webPlans.find(
        (p) => p.name.includes(targetName) && p.billing_period === billingPeriod,
      );
      return found?.product_id;
    },
    [isNativeApp, getIdentifier, webPlans],
  );

  const purchaseRevenueCat = useCallback(
    async (productId: string) => {
      if (!user?.uid) return;
      handleDialog({ type: DialogType.LOADING, value: true });
      try {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            command: "subscription",
            data: { uid: user.uid, sku: productId },
          }),
        );
      } catch (err) {
        console.error("Failed to start RevenueCat purchase:", err);
        recordPurchaseResult(AnalyticsEventType.PURCHASE_ERROR, "post_message_failed");
        handleDialog({ type: DialogType.LOADING, value: false });
        showToast(I18n.t("MSG_USER_PLAN_PURCHASE_ERROR"));
      }
    },
    [handleDialog, user?.uid],
  );

  const purchaseHostedCheckout = useCallback(
    async (productId: string) => {
      if (!user?.uid) return;
      handleDialog({ type: DialogType.LOADING, value: true });
      try {
        const result = await dispatch(
          createHostedCheckout({
            uid: user.uid,
            product_id: productId,
            success_url:
              options?.successUrl ??
              `${window.location.origin}/user?tab=${UserPageTab.PLAN}&subTab=${PlanSubTab.CURRENT}`,
            cancel_url:
              options?.cancelUrl ??
              `${window.location.origin}/user?tab=${UserPageTab.PLAN}&subTab=${PlanSubTab.PURCHASE}`,
          }),
        ).unwrap();
        window.location.href = result.checkout_url;
      } catch (err) {
        console.error("Failed to create hosted checkout:", err);
        recordPurchaseResult(AnalyticsEventType.PURCHASE_ERROR, "checkout_create_failed");
        handleDialog({ type: DialogType.LOADING, value: false });
        showToast(I18n.t("MSG_USER_PLAN_PURCHASE_ERROR"));
      }
    },
    [dispatch, handleDialog, user?.uid, options?.successUrl, options?.cancelUrl],
  );

  const purchase = useCallback(
    async (planCategory: PlanCategory, billingPeriod: BillingPeriod, source: string) => {
      if (platform === AppPlatform.iOS && Number(window.kidsBuildVersion) <= IOS_MIN_BUILD) {
        setShowVersionWarning(true);
        return;
      }
      if (
        platform === AppPlatform.Android &&
        Number(window.kidsBuildVersion) <= ANDROID_MIN_BUILD
      ) {
        setShowVersionWarning(true);
        return;
      }

      recordPurchaseStart({
        plan_category: planCategory,
        billing_period: billingPeriod,
        store: getPurchaseStore(platform),
        source,
      });
      setPurchaseAudience(options?.audience ?? PurchaseAudience.CHILD);

      const productId = getProductId(planCategory, billingPeriod);
      if (!productId) {
        recordPurchaseResult(AnalyticsEventType.PURCHASE_ERROR, "product_not_found");
        showToast(I18n.t("MSG_USER_PLAN_PURCHASE_ERROR"));
        return;
      }

      if (isNativeApp) {
        await purchaseRevenueCat(productId);
      } else {
        await purchaseHostedCheckout(productId);
      }
    },
    [
      platform,
      isNativeApp,
      getProductId,
      purchaseRevenueCat,
      purchaseHostedCheckout,
      options?.audience,
    ],
  );

  const closeVersionWarning = useCallback(() => setShowVersionWarning(false), []);

  const gotoStoreUpdate = useCallback(() => {
    if (platform === AppPlatform.iOS) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "https://apps.apple.com/jp/app/id1642655214" },
        }),
      );
    } else if (platform === AppPlatform.Android) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "https://play.google.com/store/apps/details?id=com.inexus.app.kids" },
        }),
      );
    } else if (platform === AppPlatform.Amazon) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          command: "openLink",
          data: { url: "http://www.amazon.co.jp/gp/mas/dl/android?p=com.inexus.app.kids" },
        }),
      );
    }
    setShowVersionWarning(false);
  }, [platform]);

  return {
    purchase,
    showVersionWarning,
    closeVersionWarning,
    gotoStoreUpdate,
    getPriceDisplay,
    isNativeApp,
    packagesStatus,
  };
};
